import React, { useEffect, useState } from 'react';
import courseService from '../services/course.service';

const normaliseCoursesResponse = (payload) => {
  if (Array.isArray(payload)) {
    return payload.slice().sort((a, b) => {
      const levelA = Number(a.class_level ?? a.classLevel ?? a.class);
      const levelB = Number(b.class_level ?? b.classLevel ?? b.class);

      if (Number.isFinite(levelA) && Number.isFinite(levelB)) {
        return levelA - levelB;
      }

      if (Number.isFinite(levelA)) {
        return -1;
      }

      if (Number.isFinite(levelB)) {
        return 1;
      }

      const nameA = (a.name || '').toString();
      const nameB = (b.name || '').toString();
      return nameA.localeCompare(nameB);
    });
  }

  if (payload && Array.isArray(payload.results)) {
    return normaliseCoursesResponse(payload.results);
  }

  return [];
};

const formatExamList = (examDetails) => {
  if (!Array.isArray(examDetails)) {
    return '—';
  }

  const sortedDetails = sortByExamPriority(examDetails);

  const names = sortedDetails
    .map((exam) => {
      const name = exam?.name ? String(exam.name).trim() : '';
      const description = exam?.description ? String(exam.description).trim() : '';

      if (!name && !description) {
        return null;
      }

      if (name && description) {
        return `${name} (${description})`;
      }

      return name || description;
    })
    .filter(Boolean);

  return names.length > 0 ? names.join(', ') : '—';
};

const resolveLevelLabel = (course) => {
  const candidates = [
    course.class_display,
    course.className,
    course.class_name,
    course.class,
    course.class_level,
    course.classLevel,
  ];

  const value = candidates.find((item) => item !== undefined && item !== null && item !== '');

  if (value === undefined) {
    return '—';
  }

  const numeric = Number(value);
  if (Number.isFinite(numeric)) {
    return `Level ${numeric}`;
  }

  return String(value);
};

const sortByExamPriority = (examDetails) => {
  if (!Array.isArray(examDetails)) {
    return [];
  }

  const PRIORITY = ['Engineering', 'Medical', 'Foundation', 'Board'];
  const getPriority = (name) => {
    const index = PRIORITY.indexOf(name);
    return index === -1 ? Number.MAX_SAFE_INTEGER : index;
  };

  return [...examDetails].sort((a, b) => {
    const nameA = a?.name || '';
    const nameB = b?.name || '';
    const priorityDiff = getPriority(nameA) - getPriority(nameB);

    if (priorityDiff !== 0) {
      return priorityDiff;
    }

    return nameA.localeCompare(nameB);
  });
};

const CoursesPage = () => {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;

    const fetchCourses = async () => {
      try {
        setLoading(true);
        setError('');

        const response = await courseService.getCourses();
        if (!active) {
          return;
        }

        setCourses(normaliseCoursesResponse(response));
      } catch (err) {
        if (!active) {
          return;
        }
        console.error('Failed to load courses', err);
        setError('Unable to load courses right now. Please try again soon.');
        setCourses([]);
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    fetchCourses();

    return () => {
      active = false;
    };
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bg-default">
      <div className="page-container py-12">
        <div className="mb-10">
          <h1 className="text-3xl font-bold text-primary">Our Courses</h1>
          <p className="text-text-secondary mt-2">Explore the learning tracks curated for every class and exam.</p>
        </div>

        {error && (
          <div className="mb-6 rounded-lg border border-error/30 bg-error/10 px-4 py-3 text-sm text-error">
            {error}
          </div>
        )}

        {courses.length === 0 && !error ? (
          <div className="py-16 text-center text-text-secondary">
            <p className="text-lg font-semibold text-primary mb-2">Courses will be published soon.</p>
            <p>We are curating programs that match your aspirations. Check back shortly.</p>
          </div>
        ) : (
          <div className="overflow-x-auto rounded-lg border border-border bg-surface">
            <table className="min-w-full divide-y divide-border">
              <thead className="bg-primary/5">
                <tr className="text-left text-sm font-semibold text-primary">
                  <th className="px-6 py-4">Course</th>
                  <th className="px-6 py-4">Level</th>
                  <th className="px-6 py-4">For Exams</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border text-sm text-text-secondary">
                {courses.map((course) => (
                  <tr key={course.id || course.name} className="hover:bg-primary/5 transition-colors">
                    <td className="px-6 py-4 font-semibold text-text-secondary">{course.name || 'Untitled Course'}</td>
                    <td className="px-6 py-4">{resolveLevelLabel(course)}</td>
                    <td className="px-6 py-4">{formatExamList(course.exam_details)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default CoursesPage;